import { useEffect, useMemo, useState } from "react";
import { Camera, CaretLeft, ImagesSquare, Hourglass, ShieldCheck, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuthGate } from "@/lib/hooks/useAuthGate";
import SessionExpiredState from "@/components/auth/SessionExpiredState";
import {
  listChildrenInClassroom,
  listTeacherClassrooms,
} from "@/lib/dailyReports";
import {
  listPhotosForChildren,
  uploadChildPhoto,
  deleteChildPhoto,
  formatPhotoDate,
} from "@/lib/photos";
import PhotoTile from "@/components/photos/PhotoTile";
import PhotoLightbox from "@/components/photos/PhotoLightbox";
import useFocusTrap from "@/lib/hooks/useFocusTrap";

/**
 * Teacher · /teacher/photos
 *
 * Simple camera-first upload UX:
 *   1. Pick a child from the classroom (chip row).
 *   2. Tap the capture tile → native camera (mobile) or file picker (desktop).
 *   3. Optional caption bottom sheet, then save.
 * The recent gallery is grouped by child with a lightbox view.
 */
export default function TeacherPhotos() {
  const { status, profile } = useAuthGate();
  const [classroom, setClassroom] = useState(null);
  const [children, setChildren] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [activeChildId, setActiveChildId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingFile, setPendingFile] = useState(null);   // { file, childId, dataUrl }
  const [caption, setCaption] = useState("");
  const [saving, setSaving] = useState(false);
  const [lightboxStart, setLightboxStart] = useState(null);

  useEffect(() => {
    if (status !== "ready") {
      if (status === "session_expired") setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const classrooms = await listTeacherClassrooms(profile.id);
        const first = classrooms[0];
        if (!first) return;
        const kids = await listChildrenInClassroom(first.id);
        const ps = await listPhotosForChildren(kids.map((k) => k.id));
        if (!alive) return;
        setClassroom(first);
        setChildren(kids);
        setActiveChildId(kids[0]?.id || null);
        setPhotos(ps);
      } catch (err) {
        toast.error(err.message || "Could not load classroom");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [status, profile?.id]);

  const activeChild = useMemo(
    () => children.find((c) => c.id === activeChildId),
    [children, activeChildId],
  );

  const photosForActive = useMemo(
    () => photos.filter((p) => p.child_id === activeChildId),
    [photos, activeChildId],
  );

  const childrenById = useMemo(
    () => Object.fromEntries(children.map((c) => [c.id, c])),
    [children],
  );

  async function handleFile(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !activeChild) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please pick an image.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Photo too large — limit is 8 MB.");
      return;
    }
    const dataUrl = await fileToDataUrl(f);
    setPendingFile({ file: f, dataUrl, childId: activeChild.id });
    setCaption("");
  }

  async function saveUpload() {
    if (!pendingFile || !activeChild) return;
    setSaving(true);
    try {
      const row = await uploadChildPhoto({
        child: activeChild,
        file: pendingFile.file,
        uploadedBy: profile.id,
        caption,
      });
      setPhotos((prev) => [row, ...prev]);
      setPendingFile(null);
      setCaption("");
      toast.success("Photo uploaded — waiting for admin approval");
    } catch (err) {
      toast.error(err.message || "Could not upload photo");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(photo) {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this photo? Parents won't see it any more.")) return;
    try {
      await deleteChildPhoto({ id: photo.id, storagePath: photo.storage_path });
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err.message || "Could not delete");
    }
  }

  if (status === "session_expired") {
    return <SessionExpiredState scope="photos" testId="teacher-photos-session-expired" />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (!classroom) {
    return (
      <div data-testid="teacher-photos-empty" className="rounded-2xl border border-neutral-200 bg-white p-8 text-center">
        <ImagesSquare size={28} weight="duotone" className="mx-auto text-neutral-400" />
        <p className="mt-3 text-sm text-neutral-600">
          You aren't assigned to a classroom yet. Ask your admin to set that up.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="teacher-photos" className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          {classroom.name}
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Classroom photos
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Snap a photo, tag the child, and an admin will review it before
          parents see it. Most go through quickly — keep it to one moment per
          photo.
        </p>
      </header>

      {/* Trust-and-safety banner — calmly tells the teacher what happens
          to their uploads. Reads as helpful context, not a warning. */}
      <section
        data-testid="teacher-photos-moderation-banner"
        className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/70 p-4 text-amber-900"
      >
        <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800">
          <ShieldCheck size={16} weight="duotone" />
        </div>
        <div className="text-sm leading-relaxed">
          <p className="font-semibold">Photos wait for admin approval</p>
          <p className="mt-0.5 text-amber-800/90">
            New uploads stay in <strong>Awaiting review</strong> until an admin
            approves them. You'll still see them here so you know what's pending.
          </p>
        </div>
      </section>

      {/* Child picker */}
      <section aria-label="Pick a child">
        <div
          data-testid="teacher-photos-child-chips"
          className="flex flex-wrap gap-2 overflow-x-auto pb-1"
        >
          {children.map((c) => {
            const active = c.id === activeChildId;
            return (
              <button
                key={c.id}
                type="button"
                data-testid={`teacher-photos-child-chip-${c.id}`}
                data-active={active}
                onClick={() => setActiveChildId(c.id)}
                className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
                  active
                    ? "border-primary/40 bg-primary/10 text-primary-hover"
                    : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                }`}
              >
                {c.first_name} {c.last_name?.[0] || ""}
              </button>
            );
          })}
        </div>
      </section>

      {/* Capture tile */}
      {activeChild && (
        <section>
          <label
            data-testid="teacher-photos-capture-label"
            className="flex cursor-pointer items-center gap-4 rounded-2xl border-2 border-dashed border-neutral-300 bg-neutral-50 p-6 transition-all hover:border-primary/40 hover:bg-primary/5 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20"
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Camera size={24} weight="duotone" className="text-primary-hover" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-base font-semibold text-neutral-900">
                Add a photo for {activeChild.first_name}
              </p>
              <p className="text-sm text-neutral-500">
                Tap to use your camera or pick one from your device.
              </p>
            </div>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              data-testid="teacher-photos-capture-input"
              onChange={handleFile}
              className="sr-only"
            />
          </label>
        </section>
      )}

      {/* Recent photos for active child */}
      {activeChild && (
        <section data-testid={`teacher-photos-gallery-${activeChild.id}`}>
          <h2 className="mb-3 font-heading text-base font-semibold text-neutral-900">
            Recent — {activeChild.first_name}
          </h2>
          {photosForActive.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-6 text-center text-sm text-neutral-500">
              No photos for {activeChild.first_name} yet. Snap one above.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
              {photosForActive.map((p, i) => {
                const pending = p.moderation_status === "pending";
                const flagged = p.moderation_status === "flagged";
                return (
                  <div key={p.id} className="relative">
                    <PhotoTile
                      photo={p}
                      data-testid={`teacher-photo-${p.id}`}
                      onOpen={() =>
                        setLightboxStart(photosForActive.findIndex((x) => x.id === p.id))
                      }
                    />
                    {(pending || flagged) && (
                      <span
                        data-testid={`teacher-photo-${p.id}-status`}
                        data-status={p.moderation_status}
                        className={`absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide backdrop-blur-sm ${
                          pending
                            ? "bg-amber-100/95 text-amber-900"
                            : "bg-[#C6851E]/95 text-white"
                        }`}
                      >
                        <Hourglass size={9} weight="bold" />
                        {pending ? "Pending" : "Reported"}
                      </span>
                    )}
                    {p.uploaded_by === profile.id && (
                      <button
                        type="button"
                        data-testid={`teacher-photo-${p.id}-delete`}
                        onClick={() => handleDelete(p)}
                        className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/50 text-white hover:bg-[#C6851E]"
                        aria-label="Delete photo"
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Caption sheet */}
      {pendingFile && (
        <CaptionSheet
          pendingFile={pendingFile}
          caption={caption}
          setCaption={setCaption}
          saving={saving}
          onCancel={() => !saving && setPendingFile(null)}
          onSave={saveUpload}
        />
      )}

      {lightboxStart !== null && (
        <PhotoLightbox
          photos={photosForActive}
          initialIndex={lightboxStart}
          childrenById={childrenById}
          onClose={() => setLightboxStart(null)}
        />
      )}
    </div>
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/**
 * Caption bottom-sheet. Extracted to its own component so the focus-trap
 * hook only runs while the sheet is mounted — keeping the hook's lifecycle
 * tied to the actual open state.
 */
function CaptionSheet({ pendingFile, caption, setCaption, saving, onCancel, onSave }) {
  const trapRef = useFocusTrap({ active: true, onEscape: onCancel });
  return (
    <div
      data-testid="teacher-photos-caption-sheet"
      className="fixed inset-0 z-50 flex items-end bg-black/40 sm:items-center sm:justify-center"
      onClick={onCancel}
    >
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="teacher-photos-caption-title"
        className="w-full max-w-md rounded-t-3xl bg-white p-5 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            aria-label="Close caption sheet"
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-neutral-100 disabled:opacity-60"
          >
            <CaretLeft size={16} weight="bold" />
          </button>
          <h2 id="teacher-photos-caption-title" className="font-heading text-lg font-semibold text-neutral-900">
            Add a caption
          </h2>
        </div>
        <img
          src={pendingFile.dataUrl}
          alt="preview"
          className="mb-3 max-h-64 w-full rounded-2xl object-cover"
        />
        <textarea
          data-testid="teacher-photos-caption-input"
          value={caption}
          onChange={(e) => setCaption(e.target.value.slice(0, 280))}
          placeholder="Optional — what's happening?"
          rows={3}
          className="w-full rounded-2xl border border-neutral-200 p-3 text-sm focus:border-primary focus:outline-none"
        />
        <p className="mt-1 text-right text-[11px] text-neutral-500">
          {caption.length}/280
        </p>
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="teacher-photos-caption-save"
            onClick={onSave}
            disabled={saving}
            className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save photo"}
          </button>
        </div>
      </div>
    </div>
  );
}
